// a component that renders individual preview panes created by the Preview generator

import { useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { fireMoveCursortoPageEvent, goToPage } from "../../lib/editor/events";

import { text } from "@quickpoint.me/shared";
import styled from "styled-components";
import { ViewerPageIndex } from "../../state/atoms";
import { PageCount } from "../../state/codeSelector";
import { PreviewImageFamily } from "../../state/preview";
import { StackCfKeyAtom } from "../../state/stackAtoms";
import RefreshSpinner from "../Refresh";

const StyledPreviewImage = styled.div<{
	$landscape: boolean;
	$isActive: boolean;
}>`
    display: flex;
    flex-direction: column;
    align-items: center;
    box-shadow: 0px 0px 5px #00000010;
    border: ${(props) =>
			props.$isActive
				? "3px solid #4A90D9"
				: `1px solid ${props.theme.navigationFrame}`};
    aspect-ratio: ${(props) => (props.$landscape ? "16/9" : "9/16")};
    height: 100%;
    cursor: pointer;
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    };

    &:hover {
        border: 2px solid ${(props) => props.theme.active || "#6c5ce7"};
    }

    &:focus-visible {
        outline: none;
        border: 3px solid #4A90D9;
        box-shadow: 0 0 0 2px rgba(74, 144, 217, 0.3);
    }
`;

const StyledPageNumber = styled.div`
    bottom: 4px;
    left: 4px;
    color: #fff;
    background: rgba(0, 0, 0, 0.85); /* worst case over white: ~10.9:1 (0.7 gave 5.7:1) */
    min-width: 1.5rem;
    height: 1.5rem;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.5rem;
    position: absolute;
    text-align: center;
    font-family: ${(props) => props.theme.font};
    border-radius: 3px;
`;

const StyledPreviewPane = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-self: center;
    height: 85%;
    align-self: center;
    margin-right: 0.75rem;
    position: relative;
    flex-shrink: 0;
    `;

function PreviewPane({
	$hostId,
	$landscape,
	cssId,
	pageIndex,
	stackCfKey,
}: {
	$hostId: string;
	$landscape: boolean;
	cssId: string;
	pageIndex: number;
	stackCfKey: string;
}) {
	const dataUrl = useRecoilValue(
		PreviewImageFamily({ stackCfKey, pageId: cssId + pageIndex }),
	);
	const currentViewerPage = useRecoilValue(ViewerPageIndex);
	const setViewerPageIndex = useSetRecoilState(ViewerPageIndex);
	const isActive = pageIndex === currentViewerPage;

	const handleClick = () => {
		goToPage($hostId, "QP", pageIndex);
		goToPage($hostId, "preview", pageIndex);
		fireMoveCursortoPageEvent(pageIndex);
		setViewerPageIndex(pageIndex);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			handleClick();
		}
	};

	return (
		<StyledPreviewPane>
			<StyledPreviewImage
				$landscape={$landscape}
				$isActive={isActive}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				tabIndex={0}
				role="button"
				aria-label={(text.preview.goToPage as (page: number) => string)(
					pageIndex,
				)}
				aria-current={isActive ? "page" : undefined}
			>
				{dataUrl && (
					<img
						src={dataUrl}
						alt={(text.preview.previewOfPage as (page: number) => string)(
							pageIndex,
						)}
					/>
				)}
				{!dataUrl && <RefreshSpinner showBackGround={true} dim={true} />}
			</StyledPreviewImage>

			<StyledPageNumber>{pageIndex}</StyledPageNumber>
		</StyledPreviewPane>
	);
}

const StyledPreviewBar = styled.div<{ $landscape?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    grid-area: thumbnails;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    scroll-padding-left: 0.5rem;
    scroll-snap-type: x mandatory;
    width: 100%;
    height: 100%;
    background-color: ${(props) => props.theme.navigationBackGround};
    border-top: 1px solid ${(props) => props.theme.navigationFrame};
    z-index: 1;
    padding: 0 0.5rem;
    position: relative;

    & > * {
      scroll-snap-align: start;
      scroll-snap-stop: always;
    }
`;

export function PreviewBar({
	$hostId,
	landscape,
	cssId,
}: { $hostId: string; landscape: boolean; cssId: string }) {
	const pageCount = useRecoilValue(PageCount);
	const stackCfKey = useRecoilValue(StackCfKeyAtom);

	const [allPages, setAllPages] = useState<JSX.Element[]>();
	useEffect(() => {
		const pages = [];
		for (let i = 1; i < pageCount + 1; i++) {
			pages.push(
				<PreviewPane
					$hostId={$hostId}
					key={`preview-pane-${i}`}
					$landscape={landscape}
					cssId={cssId}
					pageIndex={i}
					stackCfKey={stackCfKey}
				/>,
			);
		}
		setAllPages(pages);
	}, [pageCount, $hostId, cssId, landscape, stackCfKey]);

	return (
		<StyledPreviewBar className={cssId}>
			{allPages}
		</StyledPreviewBar>
	);
}
